import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { useProductosStore, filterValidImageUrls } from '../../stores/useProductosStore';
import { useAuthStore } from '../../stores/useAuthStore';

interface ImageGalleryProps {
  productoId: string;
  images: string[];
  onImagesChange?: (images: string[]) => void;
}

export function ImageGallery({ productoId, images = [], onImagesChange }: ImageGalleryProps) {
  const { currentEmpresa } = useAuthStore();
  const { uploadProductImages, removeProductImage, reorderProductImages, setPrimaryImage } = useProductosStore();
  const [uploadingImages, setUploadingImages] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const empresaId = currentEmpresa?.id;
  const list = filterValidImageUrls(Array.isArray(images) ? images : []);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !empresaId) return;
    setUploadingImages(true);
    try {
      const uploadedUrls = await uploadProductImages(productoId, Array.from(files));
      setToast({ type: 'success', text: `${uploadedUrls.length} imagen(es) agregada(s)` });
      if (onImagesChange) onImagesChange([...list, ...uploadedUrls]);
    } catch (err: unknown) {
      setToast({ type: 'error', text: err instanceof Error ? err.message : 'Error al subir imágenes' });
    } finally {
      setUploadingImages(false);
      e.target.value = '';
    }
  };

  const handleRemoveImage = async (index: number) => {
    if (!window.confirm('¿Eliminar esta imagen?')) return;
    try {
      await removeProductImage(productoId, index);
      setToast({ type: 'success', text: 'Imagen eliminada' });
      if (onImagesChange) onImagesChange(list.filter((_, i) => i !== index));
    } catch {
      setToast({ type: 'error', text: 'Error al eliminar imagen' });
    }
  };

  const handleReorder = async (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    try {
      await reorderProductImages(productoId, fromIndex, toIndex);
      const newImages = [...list];
      const [removed] = newImages.splice(fromIndex, 1);
      if (removed === undefined) return;
      newImages.splice(toIndex, 0, removed);
      if (onImagesChange) onImagesChange(newImages);
    } catch {
      setToast({ type: 'error', text: 'Error al reordenar' });
    }
  };

  const handleSetPrimary = async (index: number) => {
    if (index === 0) return;
    try {
      await setPrimaryImage(productoId, index);
      setToast({ type: 'success', text: 'Imagen principal actualizada' });
      const newImages = [...list];
      const [primary] = newImages.splice(index, 1);
      if (primary === undefined) return;
      newImages.unshift(primary);
      if (onImagesChange) onImagesChange(newImages);
    } catch {
      setToast({ type: 'error', text: 'Error al actualizar' });
    }
  };

  const handleDragStart = (index: number) => setDraggedIndex(index);
  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDrop = async (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;
    await handleReorder(draggedIndex, toIndex);
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-3">
      {toast && (
        <div
          className={`rounded-lg px-3 py-2 text-sm ${
            toast.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {toast.text}
        </div>
      )}
      <label className="block text-sm font-medium text-gray-700">Imágenes del Producto</label>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {list.map((url, index) => (
          <div
            key={`${url}-${index}`}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            className={`relative group rounded-lg overflow-hidden border-2 ${
              draggedIndex === index ? 'border-blue-500 opacity-50' : 'border-gray-200'
            } ${index === 0 ? 'ring-2 ring-blue-500' : ''}`}
          >
            <div className="aspect-square">
              <img src={url} alt={`Producto ${index + 1}`} className="w-full h-full object-cover" />
            </div>
            {index === 0 && (
              <div className="absolute top-2 left-2 bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium flex items-center gap-1">
                <Star className="h-3 w-3 fill-current" />
                Principal
              </div>
            )}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200">
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex gap-2">
                  {index !== 0 && (
                    <button
                      type="button"
                      onClick={() => handleSetPrimary(index)}
                      className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      title="Establecer como principal"
                    >
                      <Star className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    title="Eliminar"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
            <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => handleReorder(index, index - 1)}
                  className="p-1 bg-gray-800/75 text-white rounded hover:bg-gray-800"
                  title="Mover a la izquierda"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              )}
              {index < list.length - 1 && (
                <button
                  type="button"
                  onClick={() => handleReorder(index, index + 1)}
                  className="p-1 bg-gray-800/75 text-white rounded hover:bg-gray-800"
                  title="Mover a la derecha"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-gray-800/75 text-white px-2 py-1 rounded text-xs">Arrastra para reordenar</div>
            </div>
          </div>
        ))}
        <label className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
          {uploadingImages ? (
            <>
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mb-2" />
              <span className="text-xs text-gray-500">Subiendo...</span>
            </>
          ) : (
            <>
              <Plus className="h-8 w-8 text-gray-400 mb-2" />
              <span className="text-xs text-gray-500 text-center px-2">Agregar imágenes</span>
            </>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
            multiple
            disabled={uploadingImages || !empresaId}
          />
        </label>
      </div>
      <p className="text-xs text-gray-500">
        La primera imagen es la principal. Arrastra para reordenar o usa las flechas.
      </p>
    </div>
  );
}
